import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from '@/components/Toast';
import dayjs from 'dayjs';
import { useAuth, useWebSocket } from '@/contexts';
import { useClient } from '@/hooks';
import { AgoraPlanNameE, UniqueEventStatusE } from '@/types/cyclone/models';
import { isBetweenDates } from '@/utils/schedule';
import { EmptyObject, ErrorI } from '@/types/cyclone/requests';
import { NotificationItem } from './types';

const TODAY_DATE = new Date();

export const useNotifications = () => {
  const { session } = useAuth();
  const { client } = useClient();
  const path = useLocation().pathname;
  const queryClient = useQueryClient();
  const {
    notifications: realtimeNotifications,
    unreadCount: realtimeUnreadCount,
    markAsRead: markRealtimeAsRead,
    markAllAsRead: markAllRealtimeAsRead,
    clearNotifications: clearRealtimeNotifications
  } = useWebSocket();

  const [readNotifications, setReadNotifications] = useState<string[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showUpgradePlanAlert] = useState(false);
  const [showConnectWalletAlert, setShowConnectWalletAlert] = useState(false);
  const [showPendingAssetsUpgradeAlert, setShowPendingAssetsUpgradeAlert] = useState(false);
  const [showUpcomingEventAlert, setShowUpcomingEventAlert] = useState(false);
  const [showUnpaidPlanAlert, setShowUnpaidPlanAlert] = useState(false);
  const [planLink, setPlanLink] = useState<string | undefined>(undefined);

  const isEventPlan = session?.vendor?.plan_name === AgoraPlanNameE.EVENT;
  const isBoxfulPlan = session?.active_plan?.boxful_plan_id !== undefined;
  // Los avisos de plan y medios de cobro son del dueño: el profesional del equipo no puede
  // resolverlos y su propio vendor nunca tiene medios de cobro propios, así que le salían
  // siempre encendidos aunque el negocio los tuviera configurados.
  const isStaff = !!session?.is_staff;
  const isWalletAssociated = session?.vendor?.payment_gateways
    ? session.vendor.payment_gateways.length !== 0
    : false;

  // Load read notifications from localStorage
  useEffect(() => {
    const storedReadNotifications = localStorage.getItem('readNotifications');
    if (storedReadNotifications) {
      setReadNotifications(JSON.parse(storedReadNotifications));
    }
  }, []);

  // Update notification states based on session
  useEffect(() => {
    setShowPendingAssetsUpgradeAlert(false);

    if (
      !isStaff &&
      session?.active_plan &&
      !session?.active_plan?.is_plan_available &&
      session?.vendor?.plan_name !== AgoraPlanNameE.EVENT
    ) {
      setShowUnpaidPlanAlert(true);
    } else {
      setShowUnpaidPlanAlert(false);
    }

    if (
      !isStaff &&
      !isWalletAssociated &&
      !session?.vendor?.is_payment_bank_transfer_enabled &&
      !session?.vendor?.is_payment_cash_enabled
    ) {
      setShowConnectWalletAlert(true);
    } else {
      setShowConnectWalletAlert(false);
    }

    if (session?.has_events) {
      const upcomingEvent = session?.vendor?.unique_events?.find((event) => {
        const schedule = event.unique_events_schedules[0];
        if (schedule)
          return isBetweenDates(
            dayjs(schedule.start_at).toDate(),
            TODAY_DATE,
            dayjs(TODAY_DATE).add(2, 'day').toDate()
          );
        else return false;
      });
      if (upcomingEvent) {
        const shouldShowAlert = upcomingEvent.status === UniqueEventStatusE.ACTIVE;
        setShowUpcomingEventAlert(shouldShowAlert);
      }
    }
  }, [path, session, queryClient, isWalletAssociated, isStaff]);

  const getAssets = () => {
    const assets = [];
    if (
      session?.vendor?.plan_name === AgoraPlanNameE.EVENT &&
      (session?.has_services || session?.has_subscriptions)
    ) {
      if (session?.has_services) assets.push('servicios');
      if (session?.has_subscriptions) assets.push('plan mensual');
    }
    return assets;
  };

  const renderShowUpcomingEventAlert = (): NotificationItem => {
    const events = session?.vendor?.unique_events
      ?.filter((event) => event.status === UniqueEventStatusE.ACTIVE)
      .sort((a, b) => {
        const aFirstSchedule = a.unique_events_schedules?.[0]?.start_at;
        const bFirstSchedule = b.unique_events_schedules?.[0]?.start_at;

        if (!aFirstSchedule || !bFirstSchedule) {
          return 0;
        }

        return aFirstSchedule > bFirstSchedule ? 1 : -1;
      });

    const nextEvent = events?.[0];
    const schedule = nextEvent?.unique_events_schedules[0];
    const isToday = schedule?.start_at === TODAY_DATE;

    const message = isToday
      ? `¡Hoy es tu ${isEventPlan ? 'evento' : 'capacitación'}! Escaneá las entradas`
      : `¡Falta poco para tu ${isEventPlan ? 'evento' : 'capacitación'}! Mirá los participantes`;

    const link = isToday
      ? `/eventos/${nextEvent?.id}/registro`
      : `/eventos/${nextEvent?.id}/editar/participantes`;

    return {
      id: '5',
      message: `${message}`,
      cta: 'acá',
      link: link,
      show: showUpcomingEventAlert,
      type: 'info',
      isRead: readNotifications.includes('5')
    };
  };

  const notifications: NotificationItem[] = useMemo(
    () => [
      {
        id: '1',
        message:
          'Alcanzaste tus 14 dias de prueba. Aboná tu Plan Profesional para continuar vendiendo sin límites.',
        cta: 'Aboná aca',
        link: '',
        show: showUpgradePlanAlert,
        type: 'info',
        onClick: () => {
          if (planLink) window.open(planLink, '_blank');
          else toast.error('No se pudo obtener el link del plan');
        },
        isRead: readNotifications.includes('1')
      },
      {
        id: '2',
        message: 'Tu plan Profesional está inactivo por falta de pago.',
        cta: 'Reactivalo acá o contactanos por cualquier duda.',
        link: '',
        onClick: () => {
          if (isBoxfulPlan) {
            if (planLink) window.open(planLink, '_blank');
            else toast.error('No se pudo obtener el link del plan');
          } else {
            setShowConfirmation(true);
          }
        },
        type: 'warning',
        show: showUnpaidPlanAlert,
        isRead: readNotifications.includes('2')
      },
      {
        id: '3',
        message: 'Para que tus servicios puedan ser contratados, tenés que habilitar tus medios de cobro.',
        cta: 'Ir ahora',
        link: '/ajustes/medios-de-cobro',
        show: showConnectWalletAlert,
        type: 'important',
        isRead: false
      },
      {
        id: '4',
        message: `Para poder vender ${getAssets().join(', ')} necesitas mejorar tu plan,`,
        cta: 'elegí el tuyo acá',
        link: '/planes',
        show: showPendingAssetsUpgradeAlert,
        type: 'important',
        isRead: false
      },
      renderShowUpcomingEventAlert()
    ],
    [
      showUpgradePlanAlert,
      showUnpaidPlanAlert,
      showConnectWalletAlert,
      showPendingAssetsUpgradeAlert,
      showUpcomingEventAlert,
      readNotifications,
      planLink,
      isBoxfulPlan,
      getAssets,
      renderShowUpcomingEventAlert
    ]
  );

  const handleRead = (id: string) => {
    const updatedReadNotifications = [...readNotifications, id];
    setReadNotifications(updatedReadNotifications);
    localStorage.setItem('readNotifications', JSON.stringify(updatedReadNotifications));
  };

  const handleMarkAllAsRead = () => {
    markAllRealtimeAsRead();
    const allNotificationIds = notifications.filter((n) => n.show && n.type !== 'important').map((n) => n.id);
    setReadNotifications((prev) => [...prev, ...allNotificationIds]);
    localStorage.setItem('readNotifications', JSON.stringify([...readNotifications, ...allNotificationIds]));
  };

  const handleClearAll = () => {
    // Clear real-time notifications
    clearRealtimeNotifications();

    // Clear system notifications by marking them all as read (they'll disappear from view)
    const allNotificationIds = notifications.filter((n) => n.show).map((n) => n.id);
    setReadNotifications((prev) => [...prev, ...allNotificationIds]);
    localStorage.setItem('readNotifications', JSON.stringify([...readNotifications, ...allNotificationIds]));
  };

  const systemUnreadCount =
    notifications.filter((notification) => notification.show && !notification.isRead).length || 0;

  const totalUnreadCount = systemUnreadCount + realtimeUnreadCount;

  const mutation = useMutation<EmptyObject, ErrorI>({
    mutationFn: () =>
      client<EmptyObject>(`me/vendor/plans/notify`, 'POST', {
        isAuthRequired: true
      }),

    onSuccess: () => {
      toast.success('Se ha notificado exitosamente al equipo.');
    }
  });

  const { data: planLinkData } = useQuery<{ link: string }>({
    queryKey: ['vendor_plan', 'link'],

    queryFn: async () =>
      await client<{ link: string }>(`users/${session?.vendor?.user_id}/plans/link`, 'GET', {
        isAuthRequired: true
      }),

    retry: false,
    refetchOnWindowFocus: false,
    enabled: showUpgradePlanAlert
  });

  useEffect(() => {
    if (planLinkData) {
      setPlanLink(planLinkData.link);
    }
  }, [planLinkData]);

  return {
    // State
    notifications,
    realtimeNotifications,
    totalUnreadCount,
    showConfirmation,

    // Handlers
    handleRead,
    handleMarkAllAsRead,
    handleClearAll,
    markRealtimeAsRead,
    setShowConfirmation,

    // Mutations
    mutation
  };
};
